import axios from 'axios';
import {
  Payee,
  Payer,
  BankAccount,
  PaymentMethod,
  CreatePayeeRequest,
  UpdatePayeeRequest,
  CreatePayerRequest,
  UpdatePayerRequest,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
  BankAccountTransferRequest,
  CreatePaymentMethodRequest,
  UpdatePaymentMethodRequest,
  AccountPayable,
  UpdateAccountPayableRequest,
  CreateAccountPayableRequest,
  AccountReceivable,
  CreateAccountReceivableRequest,
  UpdateAccountReceivableRequest,
  BankTransaction,
  AnalyticsSummary,
  CashflowItem,
  CreateInvestmentRequest,
  Investment,
  InvestmentType,
  User,
  ExpenseCategory,
  CreateExpenseCategoryRequest,
  UpdateExpenseCategoryRequest,
  Expense,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  ExpenseMonthlyReport,
  AuditLog,
  AuditAction,
} from '../types';

/** Base da API: dev em `.env`, produção em `.env.production` (CRA injeta em build). */
const API_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/$/, '') ||
  'http://localhost:3000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
});

const notifyAuthUserChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-user-updated'));
  }
};

// Simple token storage helpers
export const authTokenStorage = {
  get: (): string | null => {
    try {
      // Try cookie first
      const match = document.cookie.match(/(?:^|; )auth_token=([^;]*)/);
      if (match && match[1]) return decodeURIComponent(match[1]);
      // Fallback to localStorage
      return localStorage.getItem('auth_token');
    } catch { return null; }
  },
  set: (token: string) => {
    try {
      // Cookie (session cookie)
      document.cookie = `auth_token=${encodeURIComponent(token)}; path=/; SameSite=Lax`;
    } catch {}
    try { localStorage.setItem('auth_token', token); } catch {}
  },
  clear: () => {
    try { document.cookie = 'auth_token=; Max-Age=0; path=/; SameSite=Lax'; } catch {}
    try { localStorage.removeItem('auth_token'); } catch {}
  }
};

// Store authenticated user basic info (e.g., id, name, username)
export interface AuthenticatedUser {
  id: string;
  name: string;
  username?: string;
}

export interface AuthSuccessResponse {
  id: string;
  name: string;
  token: string;
  username?: string;
}

export interface TwoFactorChallengeResponse {
  requiresTwoFactor: boolean;
  challengeId: string;
}

export interface TwoFactorValidateRequest {
  challengeId: string;
  token: string;
}

export interface TwoFactorEnableResponse {
  qrDataUrl: string;
}

export interface UpdateUserProfileRequest {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
}

export interface AuthTokenPayload {
  username?: string;
  sub?: string;
  twoFactorEnabled?: boolean;
  [key: string]: unknown;
}

export const authUserStorage = {
  get: (): AuthenticatedUser | null => {
    try {
      const raw = localStorage.getItem('auth_user');
      return raw ? JSON.parse(raw) as AuthenticatedUser : null;
    } catch { return null; }
  },
  set: (user: AuthenticatedUser) => {
    try { localStorage.setItem('auth_user', JSON.stringify(user)); } catch {}
    notifyAuthUserChanged();
  },
  clear: () => {
    try { localStorage.removeItem('auth_user'); } catch {}
    notifyAuthUserChanged();
  }
};

export const persistAuthenticatedSession = (data: AuthSuccessResponse) => {
  authTokenStorage.set(data.token);
  const username = data.username || getUsernameFromToken() || undefined;
  authUserStorage.set({
    id: data.id,
    name: data.name,
    username,
  });
  return {
    username,
    isAdmin: username === 'admin',
  };
};

export const getAuthTokenPayload = (): AuthTokenPayload | null => {
  try {
    const token = authTokenStorage.get();
    if (!token) return null;
    
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode payload (base64url)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as AuthTokenPayload;
  } catch {
    return null;
  }
};

// Helper to decode JWT token and extract username
export const getUsernameFromToken = (): string | null => {
  const payload = getAuthTokenPayload();
  return payload?.username || payload?.sub || null;
};

export const getTwoFactorEnabledFromToken = (): boolean => {
  return Boolean(getAuthTokenPayload()?.twoFactorEnabled);
};

// Attach Authorization header except for auth/user creation/registration
api.interceptors.request.use((config) => {
  const url = config.url || '';
  const method = (config.method || 'get').toLowerCase();
  const isAuth = url.includes('/user/auth');
  const isTwoFactorValidate = url.includes('/two-factor/validate');
  const isUserCreate = url.includes('/user') && method === 'post' && !isAuth;
  const isUserRegister = url.match(/\/user\/[^/]+\/?$/) && method === 'post';
  if (!isAuth && !isTwoFactorValidate && !isUserCreate && !isUserRegister) {
    const token = authTokenStorage.get();
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any)['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
});

// Global 401 handler: clear token and redirect to login
let isRedirectingToLogin = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const url: string = error?.config?.url || '';
      const isAuthRequest = url.includes('/user/auth');
      const isTwoFactorValidate = url.includes('/two-factor/validate');
      if (status === 401 && !isAuthRequest && !isTwoFactorValidate && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        authTokenStorage.clear();
        authUserStorage.clear();
        // Use replace to avoid back navigation into a protected route
        window.location.replace('/login');
      }
    } catch {}
    return Promise.reject(error);
  }
);

// Payee endpoints
export const payeeApi = {
  getAll: () => api.get<Payee[]>('/payee'),
  create: (data: CreatePayeeRequest) => api.post<Payee>('/payee', data),
  update: (id: string, data: UpdatePayeeRequest) => api.patch<Payee>(`/payee/${id}`, data),
};

// Payer endpoints
export const payerApi = {
  getAll: () => api.get<Payer[]>('/payer'),
  create: (data: CreatePayerRequest) => api.post<Payer>('/payer', data),
  update: (id: string, data: UpdatePayerRequest) => api.patch<Payer>(`/payer/${id}`, data),
};

// Bank Account endpoints
export const bankAccountApi = {
  getAll: () => api.get<BankAccount[]>('/bank-account'),
  create: (data: CreateBankAccountRequest) => api.post<BankAccount>('/bank-account', data),
  update: (id: string, data: UpdateBankAccountRequest) => api.patch<BankAccount>(`/bank-account/${id}`, data),
  transfer: (data: BankAccountTransferRequest) => api.post<void>('/bank-account/transfer', data),
  getTransactions: (id: string, params?: { occurredAtStart?: string; occurredAtEnd?: string }) => 
    api.get<BankTransaction[]>(`/bank-account/${id}/transactions`, { params }),
};

// Payment Method endpoints
export const paymentMethodApi = {
  getAll: () => api.get<PaymentMethod[]>('/payment-method'),
  create: (data: CreatePaymentMethodRequest) => api.post<PaymentMethod>('/payment-method', data),
  update: (id: string, data: UpdatePaymentMethodRequest) => api.patch<PaymentMethod>(`/payment-method/${id}`, data),
};

// Account Payable endpoints
export const accountPayableApi = {
  getAll: (params?: Record<string, any>) => api.get<AccountPayable[]>('/account-payable', { params }),
  getById: (id: string) => api.get<AccountPayable>(`/account-payable/${id}`),
  create: (data: CreateAccountPayableRequest) => api.post<AccountPayable>('/account-payable', data),
  update: (id: string, data: UpdateAccountPayableRequest) => api.patch<AccountPayable>(`/account-payable/${id}`, data),
  delete: (id: string) => api.delete<void>(`/account-payable/${id}`),
  pay: (id: string, data: { paymentDate: string }) => api.post<AccountPayable>(`/account-payable/${id}/pay`, data),
  refund: (id: string) => api.post<AccountPayable>(`/account-payable/${id}/refund`),
};

// Account Receivable endpoints
export const accountReceivableApi = {
  getAll: (params?: Record<string, any>) => api.get<AccountReceivable[]>('/account-receivable', { params }),
  getById: (id: string) => api.get<AccountReceivable>(`/account-receivable/${id}`),
  create: (data: CreateAccountReceivableRequest) => api.post<AccountReceivable>('/account-receivable', data),
  update: (id: string, data: UpdateAccountReceivableRequest) => api.patch<AccountReceivable>(`/account-receivable/${id}`, data),
  delete: (id: string) => api.delete<void>(`/account-receivable/${id}`),
  receive: (id: string, data: { receiptDate: string }) => api.post<AccountReceivable>(`/account-receivable/${id}/receive`, data),
  refund: (id: string) => api.post<AccountReceivable>(`/account-receivable/${id}/refund`),
};

// Auth endpoints
export const authApi = {
  login: (data: { username: string; password: string }) =>
    api.post<AuthSuccessResponse | TwoFactorChallengeResponse>('/user/auth', data),
  validateTwoFactor: (data: TwoFactorValidateRequest) =>
    api.post<AuthSuccessResponse>('/two-factor/validate', data),
  enableTwoFactor: () => api.post<TwoFactorEnableResponse>('/two-factor/enable'),
  disableTwoFactor: () => api.post<void>('/two-factor/disable'),
  enableUserTwoFactor: (username: string) => api.post<TwoFactorEnableResponse>('/two-factor/enable', { username }),
  disableUserTwoFactor: (username: string) => api.post<void>('/two-factor/disable', { username }),
};

// User endpoints
export interface RegisterRequest {
  name: string;
  username: string;
  password: string;
}

export interface RegisterResponse {
  id: string;
  name: string;
  username: string;
  accountId: string;
  createdAt: string;
  updatedAt: string;
}

export const userApi = {
  register: (token: string, data: RegisterRequest) => api.post<RegisterResponse>(`/user/${token}`, data),
  getAll: (params?: { accountId?: string }) => api.get<User[]>('/user', { params }),
  getByAccountId: (accountId: string) => api.get<User[]>('/user', { params: { accountId } }),
  updateProfile: (data: UpdateUserProfileRequest) => api.patch('/user/profile', data),
};

// Analytics endpoints
export const analyticsApi = {
  getSummary: (params: { startDate: string; endDate: string }) => 
    api.get<AnalyticsSummary>('/analytics/summary', { params }),
  getCashflow: (params: { bankAccountId?: string; groupBy: 'day' | 'month'; startDate: string; endDate: string }) => 
    api.get<CashflowItem[]>('/analytics/cashflow', { params }),
};

// Investment endpoints
export const investmentApi = {
  create: (data: CreateInvestmentRequest) => api.post<void>('/investment', data),
  getAll: (params?: { bankAccountId?: string; occurredAtStart?: string; occurredAtEnd?: string }) =>
    api.get<Investment[]>('/investment', { params }),
  getBalance: (bankAccountId: string) => api.get<{ balance: number }>(`/investment/${bankAccountId}/balance`),
  update: (id: string, data: { amount: number; occurredAt: string; type: InvestmentType }) => api.patch<void>(`/investment/${id}`, data),
};

// Admin endpoints
export interface CreateAccountRequest {
  name: string;
}

export interface AccountResponse {
  id: string;
  name: string;
  number: number;
  createdAt: string;
  updatedAt: string;
}

export interface SendInviteRequest {
  email: string;
}

export const adminApi = {
  createAccount: (data: CreateAccountRequest) => api.post<AccountResponse>('/admin/account', data),
  getAllAccounts: () => api.get<AccountResponse[] | null>('/admin/account'),
  sendInvite: (accountId: string, data: SendInviteRequest) => api.post<void>(`/admin/account/${accountId}/invite`, data),
  updateUser: (id: string) => api.patch<User>(`/admin/user/${id}`),
};

export interface GetAuditLogsParams {
  userId?: string;
  action?: AuditAction;
  startDate?: string;
  endDate?: string;
}

export const logsApi = {
  getAll: (params?: GetAuditLogsParams) => api.get<AuditLog[]>('/logs', { params }),
};

// Expense categories
export const expenseCategoryApi = {
  getAll: () => api.get<ExpenseCategory[]>('/expense-category'),
  create: (data: CreateExpenseCategoryRequest) => api.post<ExpenseCategory>('/expense-category', data),
  update: (id: string, data: UpdateExpenseCategoryRequest) => api.patch<ExpenseCategory>(`/expense-category/${id}`, data),
  delete: (id: string) => api.delete<void>(`/expense-category/${id}`),
};

// Expenses
export const expenseApi = {
  getAll: (params?: { categoryId?: string; dateStart?: string; dateEnd?: string }) =>
    api.get<Expense[]>('/expense', { params }),
  create: (data: CreateExpenseRequest) => api.post<Expense>('/expense', data),
  update: (id: string, data: UpdateExpenseRequest) => api.patch<Expense>(`/expense/${id}`, data),
  delete: (id: string) => api.delete<void>(`/expense/${id}`),
  getMonthlyReport: (params: { year: number; month: number }) =>
    api.get<ExpenseMonthlyReport>('/expense/report', { params }),
};

export default api;
